import React, { useState } from 'react';
import { properties as propertiesApi } from '../services/api';

const backdrop = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: '1rem',
};
const box = {
  background: 'var(--surface)', borderRadius: 'var(--radius-md)', padding: '2rem',
  maxWidth: 520, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
  maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box',
};
const label = { display: 'block', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--ink)', marginBottom: '0.3rem' };
const input = {
  width: '100%', padding: '0.6rem 0.8rem', minHeight: 44, border: '1px solid var(--border-strong)',
  borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-md)', boxSizing: 'border-box', outline: 'none',
};

// Self-serve "List Your Unit For Sale" form — any logged-in owner can list
// their own unit at any resort. Submissions are marked owner_submitted so
// buyer contact always routes through on-platform messaging, never a
// shared resort inbox.
export default function ListForSaleModal({ propertyId, onClose, onSuccess }) {
  const [form, setForm] = useState({
    unitNumber: '', weekNumber: '', roomType: '', maxOccupancy: 4,
    askingPrice: '', maintenanceFee: '', contactPreference: 'email', description: '',
  });
  const [status, setStatus] = useState('idle'); // idle | saving | done | error
  const [errMsg, setErrMsg] = useState('');

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setStatus('saving');
    try {
      await propertiesApi.createForSale(propertyId, {
        unit_number: form.unitNumber,
        week_number: parseInt(form.weekNumber, 10),
        room_type: form.roomType,
        max_occupancy: parseInt(form.maxOccupancy, 10) || 4,
        asking_price_cents: Math.round(parseFloat(form.askingPrice) * 100),
        maintenance_fee_cents: Math.round(parseFloat(form.maintenanceFee) * 100),
        contact_preference: form.contactPreference,
        description: form.description || null,
      });
      setStatus('done');
    } catch (err) {
      setErrMsg(err.response?.data?.error || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  }

  return (
    <div style={backdrop} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={box}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, margin: 0 }}>List Your Unit For Sale</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 'var(--text-xl)', cursor: 'pointer', color: 'var(--ink-muted)', lineHeight: 1 }}>×</button>
        </div>

        {status === 'done' ? (
          <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
            <div style={{ fontSize: 'var(--text-2xl)', marginBottom: '0.75rem' }}>✓</div>
            <p style={{ color: 'var(--ink-muted)', fontSize: 'var(--text-base)', marginBottom: '1.25rem' }}>
              Your unit has been listed. Interested buyers will contact you through our messaging system.
            </p>
            <button
              onClick={() => (onSuccess ? onSuccess() : onClose())}
              style={{ background: 'var(--brand-dark)', color: 'var(--surface)', border: 'none', borderRadius: 'var(--radius-sm)', padding: '0.6rem 1.5rem', cursor: 'pointer', fontWeight: 600 }}
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div style={{ display: 'grid', gap: '0.85rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                <div>
                  <label style={label}>Unit Number *</label>
                  <input required style={input} value={form.unitNumber} onChange={e => set('unitNumber', e.target.value)} placeholder="e.g. 215B" />
                </div>
                <div>
                  <label style={label}>Week Number *</label>
                  <input required type="number" min="1" max="53" style={input} value={form.weekNumber} onChange={e => set('weekNumber', e.target.value)} placeholder="1-53" />
                </div>
              </div>

              <div>
                <label style={label}>Room Type *</label>
                <input required style={input} value={form.roomType} onChange={e => set('roomType', e.target.value)} placeholder="e.g. 2BR/2BA Ocean View" />
              </div>

              <div>
                <label style={label}>Max Occupancy *</label>
                <input required type="number" min="1" style={input} value={form.maxOccupancy} onChange={e => set('maxOccupancy', e.target.value)} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                <div>
                  <label style={label}>Asking Price ($) *</label>
                  <input required type="number" min="0" step="0.01" style={input} value={form.askingPrice} onChange={e => set('askingPrice', e.target.value)} placeholder="e.g. 8000" />
                </div>
                <div>
                  <label style={label}>Annual Maintenance Fee ($) *</label>
                  <input required type="number" min="0" step="0.01" style={input} value={form.maintenanceFee} onChange={e => set('maintenanceFee', e.target.value)} placeholder="e.g. 950" />
                </div>
              </div>

              <div>
                <label style={label}>Preferred Contact Method *</label>
                <div style={{ display: 'flex', gap: '1.25rem', fontSize: 'var(--text-sm)', marginTop: '0.35rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 400 }}>
                    <input type="radio" name="contactPreference" checked={form.contactPreference === 'email'} onChange={() => set('contactPreference', 'email')} /> Email
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 400 }}>
                    <input type="radio" name="contactPreference" checked={form.contactPreference === 'phone'} onChange={() => set('contactPreference', 'phone')} /> Phone
                  </label>
                </div>
              </div>

              <div>
                <label style={label}>Description</label>
                <textarea
                  style={{ ...input, minHeight: 90, resize: 'vertical', paddingTop: '0.6rem' }}
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  placeholder="Anything buyers should know — recent renovations, view, transferability, etc."
                />
              </div>
            </div>

            {status === 'error' && (
              <p style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)', marginTop: '0.75rem' }}>{errMsg}</p>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
              <button type="button" onClick={onClose} style={{ flex: 1, padding: '0.65rem', minHeight: 44, background: 'var(--surface-alt)', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontWeight: 600, color: 'var(--ink)' }}>
                Cancel
              </button>
              <button type="submit" disabled={status === 'saving'} style={{ flex: 2, padding: '0.65rem', minHeight: 44, background: 'var(--brand-dark)', color: 'var(--surface)', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontWeight: 600 }}>
                {status === 'saving' ? 'Submitting…' : 'Submit Listing'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
